/**
 * Baustein- und Buendelmodell
 * ===========================
 *
 * Ein Buendel ist eine geordnete Liste von Bausteinen. Die Karte kennt keine
 * Kombinationen -- "Event + Hotel + Flug" ist kein Typ, sondern drei Bausteine
 * untereinander. Ein neuer Baustein (Bahn, Mietwagen) ist eine neue Datei in
 * parts/ und ein Eintrag in PART_RENDERERS, sonst nichts.
 *
 * Warum getrennte Preise je Baustein Pflicht sind: verbundene Reiseleistungen
 * verlangen getrennte Vertraege und getrennt ausgewiesene Preise. Ein
 * undurchsichtiger Gesamtpreis wuerde uns zum Reiseveranstalter machen.
 * Deshalb bildet das Buendel eine SUMME, nie einen Pauschalpreis.
 *
 * Konzept: systemdocumentation/architecture/KONZEPT_buendelkarte-und-bausteine.md
 */

export const PART_TYPES = {
  EVENT: "event",
  HOTEL: "hotel",
  FLIGHT: "flight",
  PACKAGE: "package",
};

/**
 * Preiseinheiten. Der Baustein sagt selbst, worauf sich sein Preis bezieht --
 * die Karte rechnet nichts um und erfindet nichts.
 */
export const PRICE_UNITS = {
  PER_PERSON: "per_person",
  PER_NIGHT: "per_night",
  PER_ROOM: "per_room",
  TOTAL: "total",
};

/**
 * Ein Baustein:
 * {
 *   type      PART_TYPES
 *   id        eindeutig innerhalb des Buendels
 *   title     Zeile 1, das was der Reisende wiedererkennt
 *   lines     [string] Grundangaben, hoechstens drei
 *   media     { imageUrl?, badge?, caption? }
 *   price     { amount, currency, unit, from, fees? } | null  -- null heisst "kein
 *             Preis", NICHT "kostenlos". Die Karte schreibt dann "Preis auf Anfrage".
 *
 *             amount ist IMMER der Betrag, den der Reisende zahlt -- Gebuehren
 *             eingerechnet. fees: [{ label, amount }] sagt zusaetzlich, woraus
 *             er sich zusammensetzt.
 *
 *             Warum nicht getrennt lassen: eine Gebuehr ist kein eigener
 *             Vertrag und kein eigener Anbieter, also auch kein eigener
 *             Baustein. Sie gehoert zum Ticket. Sie aber aus dem angezeigten
 *             Preis herauszuhalten hiesse, einen Preis zu zeigen, den es nicht
 *             gibt -- deshalb steckt sie in amount UND steht in fees.
 *   provider  Anbietername, fuer den getrennten Ausweis
 *   anchorData  was dieser Baustein den anderen vorgibt (Datum, Koordinaten)
 *   constraints [{ kind, severity, text }] Zwaenge, die das Buendel prueft
 * }
 */

/**
 * Preis mit Gebuehren bauen. amount ist der zu zahlende Betrag, fees nennt die
 * Bestandteile. Ohne Grundpreis gibt es keinen Preis -- nicht 0.
 */
export function priceWithFees(base, fees, currency, unit, from) {
  const list = (fees || []).filter((f) => f && f.amount > 0);
  if (!(base > 0)) return null;
  return {
    amount: base + list.reduce((sum, f) => sum + f.amount, 0),
    currency: currency || "EUR",
    unit: unit || PRICE_UNITS.PER_PERSON,
    from: Boolean(from),
    fees: list,
  };
}

/** Aus einem Event-Treffer der Suche einen Baustein machen -- nur Grundangaben. */
export function eventToPart(offer) {
  const city = offer.destination || "";
  const country = offer.country || "";
  const place = [city, country].filter(Boolean).join(", ");
  const venue = offer.venue || "";

  const start = offer.eventperiodStart || offer.eventperiod_start || "";
  const end = offer.eventperiodEnd || offer.eventperiod_end || "";

  const amount = offer.pricePerPerson ?? offer.price ?? 0;

  const lines = [];
  if (offer.tournament) lines.push(offer.tournament);
  const when = formatRange(start, end);
  if (when) lines.push(when);
  const where = [place, venue].filter(Boolean).join(" · ");
  if (where) lines.push(where);

  return {
    type: PART_TYPES.EVENT,
    id: offer.id,
    title: offer.label || offer.title || "Event",
    lines,
    // Fliesstext getrennt von den Grundangaben: die drei lines sind Fakten in
    // fester Reihenfolge, description ist Prosa und darf gekuerzt werden.
    description: stripMarkdown(offer.description || ""),
    media: {
      imageUrl: offer.imageUrl || offer.image || null,
      badge: offer.eventType || offer.category || "Event",
      caption: city || country,
    },
    // amount 0 heisst: kein min_price hinterlegt -> "auf Anfrage", kein Nullpreis.
    price: amount > 0
      ? { amount, currency: offer.currency || "EUR", unit: PRICE_UNITS.PER_PERSON, from: true }
      : null,
    provider: offer.provider || "SportsEvents365",
    // Herkunft: entscheidet, welcher Adapter nach Ticketkategorien gefragt wird.
    // Leer heisst ausdruecklich "kein Anbieter hinterlegt", nicht "nimm SE365".
    source: offer.source || "",
    sourceId: offer.sourceId || "",
    anchorData: {
      date: start,
      venue,
      lat: offer.venueLat ?? null,
      lng: offer.venueLng ?? null,
      // Saalplan des ORTES, nicht der Kategorie -- Orientierung, keine Platzzusage.
      mapUrl: offer.venueMapUrl || "",
      address: offer.venueAddress || "",
      imageUrl: offer.imageUrl || offer.image || "",
      city,
      country,
    },
    constraints: [],
  };
}

/** Bildet ein Buendel aus Bausteinen. Der erste ist per Vorgabe der Anker. */
export function makeBundle(parts, options) {
  const list = (parts || []).filter(Boolean);
  const opts = options || {};
  return {
    parts: list,
    anchorId: opts.anchorId || (list[0] && list[0].id) || null,
    travellers: opts.travellers || 1,
  };
}

/**
 * Summe ueber alle Bausteine mit bekanntem Preis.
 * Gibt zusaetzlich zurueck, wie viele Bausteine keinen Preis haben -- die Karte
 * muss das sichtbar machen, statt eine Summe zu zeigen, die nicht alles enthaelt.
 */
export function bundleSum(bundle) {
  const parts = (bundle && bundle.parts) || [];
  const travellers = (bundle && bundle.travellers) || 1;

  let total = 0;
  let currency = null;
  let unpriced = 0;

  parts.forEach((part) => {
    if (!part.price || !(part.price.amount > 0)) {
      unpriced += 1;
      return;
    }
    currency = currency || part.price.currency;
    // Die Menge haengt am BAUSTEIN, nicht am Buendel: Karten und Reisende sind
    // zwei verschiedene Zahlen. Vier Freunde koennen sich zwei Karten teilen,
    // und das Hotel rechnet trotzdem mit vier. Wer keine eigene Menge traegt,
    // rechnet weiter mit den Reisenden -- so verhalten sich Hotel und Flug.
    const quantity = part.quantity > 0 ? part.quantity : travellers;
    total += part.price.unit === PRICE_UNITS.PER_PERSON
      ? part.price.amount * quantity
      : part.price.amount;
  });

  return {
    amount: total,
    currency: currency || "EUR",
    unpriced,
    complete: unpriced === 0 && parts.length > 0,
  };
}

/** Alle Zwaenge der Bausteine einsammeln, schwerste zuerst. */
export function bundleConstraints(bundle) {
  const order = { blocking: 0, warning: 1, info: 2 };
  return ((bundle && bundle.parts) || [])
    .flatMap((part) => (part.constraints || []).map((c) => ({ ...c, partId: part.id })))
    .sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9));
}

/** Die Importtexte kommen teils mit Markdown-Fettung an, die hier nichts nutzt. */
function stripMarkdown(text) {
  return String(text || "").replace(/\*\*/g, "").trim();
}

function formatRange(start, end) {
  const from = formatDate(start);
  if (!from) return "";
  const to = formatDate(end);
  return to && to !== from ? `${from} – ${to}` : from;
}

function formatDate(iso) {
  if (!iso) return "";
  const dateOnly = String(iso).split(" ")[0].split("T")[0];
  const [y, m, d] = dateOnly.split("-");
  if (!y || !m || !d) return "";
  return `${d}.${m}.${y}`;
}
